import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { db, storage } from '../firebase';
import { ref, uploadBytes, getDownloadURL, uploadBytesResumable } from 'firebase/storage';

// List of supported professional roles
const PROFESSIONAL_ROLES = [
  'Speech Therapist',
  'Occupational Therapist',
  'Behaviour Therapist (ABA)',
  'Psychologist',
  'Special Educator',
  'Shadow Teacher',
  'Pediatrician',
  'Child Psychiatrist',
  'Therapy Center Staff',
  'Special Needs Tutor',
  'Rehabilitation Counselor',
  'Music Therapist',
  'Art Therapist',
  'Physical Therapist',
  'Developmental Pediatrician',
  'Applied Behavior Analyst (ABA)'
].sort();
import { updateProfile } from 'firebase/auth';
import { motion, AnimatePresence } from 'framer-motion';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { FaEdit, FaTimes, FaCheck, FaUserCircle, FaGraduationCap, FaBriefcase, FaFilePdf, FaFileWord } from 'react-icons/fa';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ShareProfileModal from '../components/ShareProfileModal';

const ProfessionalProfile = () => {
  const { currentUser } = useAuth();
  const [profile, setProfile] = useState({
    fullName: currentUser?.displayName || '',
    email: currentUser?.email || '',
    mobile: '',
    city: '',
    expertise: [],
    bio: '',
    experience: [],
    education: [{ degree: '', institution: '', year: '' }],
    services: [],
    fee: '',
    feeType: 'per session', // 'per session' or 'per month'
    mode: 'Both', // Default to 'Both' for new profiles
    availability: {
      monday: { available: false, hours: [] },
      tuesday: { available: false, hours: [] },
      wednesday: { available: false, hours: [] },
      thursday: { available: false, hours: [] },
      friday: { available: false, hours: [] },
      saturday: { available: false, hours: [] },
      sunday: { available: false, hours: [] }
    },
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
  });
  
  // Section titles with consistent styling
  const sectionTitles = {
    personal: 'Personal Information',
    professional: 'Professional Details',
    experience: 'Work Experience',
    education: 'Education',
    services: 'Services Offered',
    availability: 'Availability'
  };
  
  // Input field styling
  const inputClass = "w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200";
  const labelClass = "block text-sm font-medium text-gray-700 mb-1";
  const sectionClass = "bg-white rounded-xl shadow-sm p-6 mb-6 border border-gray-100";
  const buttonClass = "inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200";
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteSuccess, setShowDeleteSuccess] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [newService, setNewService] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [newExpertise, setNewExpertise] = useState('');

  // Check if profile is complete
  const isProfileComplete = () => {
    return (
      profile.fullName &&
      profile.mobile &&
      profile.city &&
      profile.expertise &&
      profile.fee &&
      profile.mode
    );
  };

  // Load profile data
  useEffect(() => {
    const loadProfile = async () => {
      if (!currentUser) return;
      
      try {
        const docRef = doc(db, 'professionals', currentUser.uid);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          const data = docSnap.data();
          setProfile(prev => ({
            ...prev,
            ...data,
            expertise: Array.isArray(data.expertise) ? data.expertise : [],
            services: Array.isArray(data.services) ? data.services : [],
            experience: Array.isArray(data.experience) ? data.experience : [],
            education: Array.isArray(data.education) ? data.education : [{ degree: '', institution: '', year: '' }],
            availability: data.availability || prev.availability
          }));
          setEditMode(false);
        } else {
          // Initialize new profile with default mode
          setProfile(prev => ({
            ...prev,
            mode: 'Both',
            experience: [],
            education: [{ degree: '', institution: '', year: '' }]
          }));
          setEditMode(true); // Start in edit mode if no profile exists
        }
      } catch (error) {
        console.error('Error loading profile:', error);
        toast.error('Failed to load profile');
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [currentUser]);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfile(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Save profile
  const handleSave = async (e) => {
    e.preventDefault();
    if (!currentUser) return;
    
    // Validate mobile number
    const mobileRegex = /^[0-9]{10}$/;
    if (!profile.mobile) {
      toast.error('Mobile number is required');
      return;
    }
    
    if (!mobileRegex.test(profile.mobile)) {
      toast.error('Please enter a valid 10-digit mobile number');
      return;
    }
    
    setSaving(true);
    try {
      // Ensure mode is properly set before saving
      const profileToSave = {
        ...profile,
        // Ensure mode is one of the allowed values
        mode: ['Online', 'Offline', 'Both'].includes(profile.mode) ? profile.mode : 'Both',
        updatedAt: new Date().toISOString()
      };
      
      await setDoc(doc(db, 'professionals', currentUser.uid), profileToSave, { merge: true });
      
      toast.success('Profile updated successfully!');
      setEditMode(false);
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  // Add new service
  const addService = () => {
    if (newService.trim() && !profile.services.includes(newService.trim())) {
      setProfile(prev => ({
        ...prev,
        services: [...prev.services, newService.trim()]
      }));
      setNewService('');
    }
  };

  // Remove service
  const removeService = (index) => {
    setProfile(prev => ({
      ...prev,
      services: prev.services.filter((_, i) => i !== index)
    }));
  };

  // Handle experience field changes
  const handleExperienceChange = (index, field, value) => {
    setProfile(prev => {
      const updatedExperience = [...(prev.experience || [])];
      if (!updatedExperience[index]) {
        updatedExperience[index] = {};
      }
      updatedExperience[index] = {
        ...updatedExperience[index],
        [field]: value
      };
      return {
        ...prev,
        experience: updatedExperience
      };
    });
  };

  // Add new experience entry
  const addNewExperience = () => {
    setProfile(prev => ({
      ...prev,
      experience: [
        ...(prev.experience || []),
        {
          position: '',
          company: '',
          startDate: '',
          endDate: '',
          currentlyWorking: false,
          location: '',
          description: ''
        }
      ]
    }));
  };

  // Remove experience entry
  const removeExperience = (index) => {
    setProfile(prev => ({
      ...prev,
      experience: prev.experience.filter((_, i) => i !== index)
    }));
  };

  // Handle education field changes
  const handleEducationChange = (index, field, value) => {
    setProfile(prev => {
      const updatedEducation = [...(prev.education || [])];
      if (!updatedEducation[index]) {
        updatedEducation[index] = { degree: '', institution: '', year: '' };
      }
      updatedEducation[index] = {
        ...updatedEducation[index],
        [field]: value
      };
      return {
        ...prev,
        education: updatedEducation
      };
    });
  };

  // Add new education entry
  const addNewEducation = () => {
    setProfile(prev => ({
      ...prev,
      education: [
        ...(prev.education || []),
        { degree: '', institution: '', year: '' }
      ]
    }));
  };

  // Remove education entry
  const removeEducation = (index) => {
    setProfile(prev => ({
      ...prev,
      education: prev.education.filter((_, i) => i !== index)
    }));
  };

  // Handle resume upload
  const handleResumeUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Reset file input
    e.target.value = '';

    // Check file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size should be less than 5MB');
      return;
    }

    // Check file type
    const validTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!validTypes.includes(file.type)) {
      toast.error('Only PDF, DOC, and DOCX files are allowed');
      return;
    }

    try {
      // Show loading state
      toast.info('Uploading resume...', { autoClose: false });
      
      // Create a unique filename with timestamp
      const fileExt = file.name.split('.').pop();
      const fileName = `resume_${Date.now()}.${fileExt}`;
      const storageRef = ref(storage, `resumes/${currentUser.uid}/${fileName}`);
      
      // Define metadata
      const metadata = {
        contentType: file.type,
        customMetadata: {
          'owner': currentUser.uid,
          'uploadedBy': currentUser.uid,
          'originalName': file.name,
          'size': file.size.toString(),
          'uploadedAt': new Date().toISOString()
        }
      };

      // Upload file to Firebase Storage
      const uploadTask = uploadBytesResumable(storageRef, file, metadata);
      
      // Monitor upload progress
      uploadTask.on('state_changed',
        (snapshot) => {
          // You can add a progress bar here if needed
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          console.log('Upload is ' + progress + '% done');
        },
        (error) => {
          console.error('Upload error:', error);
          toast.dismiss();
          toast.error('Failed to upload resume: ' + error.message);
        },
        async () => {
          // Upload completed successfully, get download URL
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          
          // Update profile with new resume info
          const updatedProfile = {
            ...profile,
            resumeFileName: file.name,
            resumeUrl: downloadURL,
            resumeLastUpdated: new Date().toISOString()
          };
          
          // Save to Firestore
          await updateDoc(doc(db, 'professionals', currentUser.uid), {
            resumeFileName: file.name,
            resumeUrl: downloadURL,
            resumeLastUpdated: new Date().toISOString()
          });
          
          // Update local state
          setProfile(updatedProfile);
          
          // Show success message
          toast.dismiss();
          toast.success('Resume uploaded successfully!');
        }
      );
      
    } catch (error) {
      console.error('Error uploading resume:', error);
      toast.dismiss();
      toast.error('Failed to upload resume: ' + error.message);
    }
  };

  // Handle profile photo upload
  const handleProfilePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please select a valid image file (JPEG, PNG, GIF)');
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image size should be less than 2MB');
      return;
    }

    try {
      setIsUploading(true);
      const timestamp = Date.now();
      const fileName = `${timestamp}_${file.name}`;
      const storageRef = ref(storage, `profile-photos/${currentUser.uid}/${fileName}`);
      
      // Add metadata required by storage rules
      const metadata = {
        contentType: file.type,
        customMetadata: {
          owner: currentUser.uid,
          uploadedBy: currentUser.uid,
          uploadedAt: timestamp.toString(),
          originalName: file.name
        }
      };
      
      // Upload the file with metadata
      await uploadBytes(storageRef, file, metadata);
      const photoURL = await getDownloadURL(storageRef);
      
      // Update user's profile
      await updateDoc(doc(db, 'professionals', currentUser.uid), {
        photoURL,
        photoUpdatedAt: timestamp
      }, { merge: true });
      
      // Update local state
      setProfile(prev => ({
        ...prev,
        photoURL
      }));
      
      // Update auth user's photoURL using Firebase Auth
      await updateProfile(currentUser, {
        photoURL
      });
      
      toast.success('Profile photo updated successfully');
    } catch (error) {
      console.error('Error uploading profile photo:', error);
      toast.error('Failed to upload profile photo');
    } finally {
      setIsUploading(false);
      // Reset file input
      e.target.value = '';
    }
  };

  // State for upload popup
  const [isUploadPopupOpen, setIsUploadPopupOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');

  // Handle file selection
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  // Handle upload from popup
  const handleUploadFromPopup = async () => {
    if (!selectedFile) return;
    
    try {
      setIsUploading(true);
      const timestamp = Date.now();
      const fileName = `${timestamp}_${selectedFile.name}`;
      const storageRef = ref(storage, `profile-photos/${currentUser.uid}/${fileName}`);
      
      const metadata = {
        contentType: selectedFile.type,
        customMetadata: {
          owner: currentUser.uid,
          uploadedBy: currentUser.uid,
          uploadedAt: timestamp.toString(),
          originalName: selectedFile.name
        }
      };
      
      await uploadBytes(storageRef, selectedFile, metadata);
      const photoURL = await getDownloadURL(storageRef);
      
      await updateDoc(doc(db, 'professionals', currentUser.uid), {
        photoURL,
        photoUpdatedAt: timestamp
      }, { merge: true });
      
      setProfile(prev => ({
        ...prev,
        photoURL
      }));
      
      await updateProfile(currentUser, {
        photoURL
      });
      
      toast.success('Profile photo updated successfully');
      setIsUploadPopupOpen(false);
      setSelectedFile(null);
      setPreviewUrl('');
    } catch (error) {
      console.error('Error uploading profile photo:', error);
      toast.error('Failed to upload profile photo');
    } finally {
      setIsUploading(false);
    }
  };

  // Expertise management
  const [selectedExpertise, setSelectedExpertise] = useState('');

  const addExpertise = (e) => {
    if (e) e.preventDefault();
    if (!selectedExpertise || profile.expertise?.includes(selectedExpertise)) {
      setSelectedExpertise('');
      return;
    }
    
    setProfile(prev => ({
      ...prev,
      expertise: [...(prev.expertise || []), selectedExpertise]
    }));
    setSelectedExpertise('');
  };

  const removeExpertise = (expertiseToRemove) => {
    setProfile(prev => ({
      ...prev,
      expertise: prev.expertise.filter(item => item !== expertiseToRemove)
    }));
  };

  const availableRoles = PROFESSIONAL_ROLES.filter(
    role => !profile.expertise?.includes(role)
  );

  // Availability management
  const toggleDayAvailability = (day) => {
    setProfile(prev => ({
      ...prev,
      availability: {
        ...prev.availability,
        [day]: {
          ...prev.availability[day],
          available: !prev.availability[day]?.available,
          hours: prev.availability[day]?.hours || []
        }
      }
    }));
  };

  const addTimeSlot = (day) => {
    setProfile(prev => ({
      ...prev,
      availability: {
        ...prev.availability,
        [day]: {
          ...prev.availability[day],
          hours: [...(prev.availability[day]?.hours || []), { start: '09:00', end: '17:00' }]
        }
      }
    }));
  };

  const updateAvailability = (day, index, field, value) => {
    setProfile(prev => {
      const updatedHours = [...(prev.availability[day]?.hours || [])];
      updatedHours[index] = { ...updatedHours[index], [field]: value };
      
      return {
        ...prev,
        availability: {
          ...prev.availability,
          [day]: {
            ...prev.availability[day],
            hours: updatedHours
          }
        }
      };
    });
  };

  const removeTimeSlot = (day, index) => {
    setProfile(prev => {
      const updatedHours = [...(prev.availability[day]?.hours || [])];
      updatedHours.splice(index, 1);
      
      return {
        ...prev,
        availability: {
          ...prev.availability,
          [day]: {
            ...prev.availability[day],
            hours: updatedHours
          }
        }
      };
    });
  };

  // Handle mode change
  const handleModeChange = (e) => {
    setProfile(prev => ({
      ...prev,
      mode: e.target.value
    }));
  };

  // Toggle edit mode
  const toggleEditMode = () => {
    setEditMode(!editMode);
  };

  // Render view mode
  const renderViewMode = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Bio Section */}
      {profile.bio && (
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="border-b border-gray-200 pb-4 mb-6">
            <h2 className="text-xl font-semibold text-gray-800">Bio</h2>
          </div>
          <p className="text-gray-700 whitespace-pre-line">{profile.bio || ''}</p>
        </div>
      )}

      {/* Personal Information Section */}
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="border-b border-gray-200 pb-4 mb-6">
          <h2 className="text-xl font-semibold text-gray-800">Personal Information</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <p className="text-sm font-medium text-gray-500">Full Name</p>
            <p className="mt-1 text-gray-900">{profile.fullName || 'Not provided'}</p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">Email</p>
            <p className="mt-1 text-gray-900">{profile.email || 'Not provided'}</p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">Mobile</p>
            <p className="mt-1 text-gray-900">{profile.mobile || 'Not provided'}</p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">City</p>
            <p className="mt-1 text-gray-900">{profile.city || 'Not provided'}</p>
          </div>

        </div>
      </div>

      {/* Services Section */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h2 className="text-xl font-semibold mb-4 text-gray-800">Services Offered</h2>
        
        {profile.services?.length > 0 ? (
          <div>
            <div className="flex flex-wrap gap-2 mb-4">
              {profile.services.map((service, index) => (
                <span key={index} className="bg-blue-100 text-blue-800 text-sm font-medium px-3 py-1 rounded-full">
                  {service}
                </span>
              ))}
            </div>
            
            {profile.fee && (
              <div className="mt-2">
                <h4 className="text-sm font-medium text-gray-700">Fee</h4>
                <p className="text-gray-800">
                  ₹{profile.fee} {profile.feeType}
                </p>
              </div>
            )}
          </div>
        ) : (
          <p className="text-gray-500 mb-4">No services added yet</p>
        )}
        
        {/* Service Mode Display */}
        <div className="border-t pt-4">
          <p className="text-sm font-medium text-gray-500">Service Mode</p>
          <p className="mt-1 text-gray-900">
            {profile.mode === 'Online' && 'Online Only'}
            {profile.mode === 'Offline' && 'In-Person Only'}
            {profile.mode === 'Both' && 'Both Online & In-Person'}
            {!profile.mode && 'Not specified'}
          </p>
        </div>
      </div>

      {/* Expertise Section */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h2 className="text-xl font-semibold mb-4 text-gray-800">Expertise</h2>
        <div className="flex flex-wrap gap-2">
          {Array.isArray(profile.expertise) && profile.expertise.length > 0 ? (
            profile.expertise.map((exp, index) => (
              <span key={index} className="bg-indigo-100 text-indigo-800 text-sm font-medium px-3 py-1 rounded-full">
                {exp}
              </span>
            ))
          ) : (
            <p className="text-gray-500">No expertise areas added yet</p>
          )}
        </div>
      </div>

      {/* Availability Section */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h2 className="text-xl font-semibold mb-4 text-gray-800">Availability</h2>
        <div className="space-y-4">
          {profile.availability ? (
            Object.entries(profile.availability).map(([day, data]) => {
              if (!data || !data.available) return null;
              
              const hours = Array.isArray(data.hours) ? data.hours : [];
              return (
                <div key={day} className="flex items-start">
                  <span className="w-24 font-medium text-gray-700 capitalize mt-1">{day}</span>
                  <div className="flex-1">
                    {hours.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {hours.map((time, idx) => (
                          <span key={idx} className="bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded">
                            {time?.start || '--:--'} - {time?.end || '--:--'}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-sm text-gray-500">Available (no specific hours set)</span>
                    )}
                  </div>
                </div>
              );
            })
          ) : (
            <p className="text-gray-500">No availability information available</p>
          )}
          
          {profile.availability && !Object.values(profile.availability).some(day => day?.available) && (
            <p className="text-gray-500">No availability set for any day</p>
          )}
        </div>
      </div>

      {/* Experience Section */}
      {(profile.experience?.length > 0) && (
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-xl font-semibold mb-4 text-gray-800">Experience</h2>
          <div className="space-y-6">
            {profile.experience.map((exp, index) => (
              <div key={index} className="border-l-4 border-indigo-200 pl-4 py-1">
                <h3 className="font-medium text-gray-900">{exp.position || 'Position Not Specified'}</h3>
                <p className="text-gray-600">{exp.company}</p>
                <p className="text-sm text-gray-500">
                  {exp.startDate} - {exp.currentlyWorking ? 'Present' : exp.endDate}
                </p>
                {exp.location && <p className="text-sm text-gray-500">{exp.location}</p>}
                {exp.description && (
                  <p className="mt-2 text-gray-600">{exp.description}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Education Section */}
      {(profile.education?.length > 0) && (
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-xl font-semibold mb-4 text-gray-800">Education</h2>
          <div className="space-y-4">
            {profile.education.map((edu, index) => (
              <div key={index} className="flex items-start">
                <div className="bg-indigo-100 p-3 rounded-full mr-4">
                  <FaGraduationCap className="text-indigo-600 text-xl" />
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">{edu.degree}</h3>
                  <p className="text-gray-600">{edu.institution}</p>
                  {edu.year && <p className="text-sm text-gray-500">{edu.year}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Resume Section */}
      {profile.resumeUrl && (
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-xl font-semibold mb-4 text-gray-800">Resume</h2>
          <div className="flex items-center">
            <div className="bg-indigo-100 p-3 rounded-full mr-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <div>
              <p className="text-gray-900">{profile.resumeFileName || 'View Resume'}</p>
              <a 
                href={profile.resumeUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-indigo-600 hover:text-indigo-800 text-sm font-medium"
                download
              >
                Download Resume
              </a>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );

  // Render edit mode
  const renderEditMode = () => (
    <motion.form
      onSubmit={handleSave}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Bio Section */}
      <div className="bg-white p-6 rounded-lg shadow mb-6">
        <div className="border-b border-gray-200 pb-4 mb-6">
          <h2 className="text-xl font-semibold text-gray-800">Bio</h2>
          <p className="mt-1 text-sm text-gray-500">Tell us about yourself and your professional background</p>
        </div>
        <div className="space-y-1">
          <textarea
            name="bio"
            value={profile.bio || ''}
            onChange={handleChange}
            rows="4"
            className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            placeholder="Tell us about yourself, your experience, and your approach to therapy..."
          />
        </div>
      </div>
      {/* Personal Information Section */}
      <div className="bg-white p-6 rounded-lg shadow mb-6">
        <div className="border-b border-gray-200 pb-4 mb-6">
          <h2 className="text-xl font-semibold text-gray-800">Personal Information</h2>
          <p className="mt-1 text-sm text-gray-500">Your personal details and contact information</p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-1">
            <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-1">
              Full Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="fullName"
              id="fullName"
              value={profile.fullName}
              onChange={handleChange}
              className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="John Doe"
            />
          </div>

          <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <p className="text-gray-700">{profile.email}</p>
            <p className="text-xs text-gray-500 mt-1">Contact email (can't be changed)</p>
          </div>


          <div className="space-y-1">
            <label htmlFor="mobile" className="block text-sm font-medium text-gray-700 mb-1">
              Mobile <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              name="mobile"
              id="mobile"
              value={profile.mobile}
              onChange={handleChange}
              className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="Enter 10-digit mobile number"
              required
              pattern="[0-9]{10}"
              title="Please enter a valid 10-digit mobile number"
            />
          </div>

          <div className="space-y-1">
            <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
              City <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="city"
              id="city"
              value={profile.city}
              onChange={handleChange}
              className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="e.g. Mumbai, Delhi, Bangalore"
            />
          </div>

          <div className="space-y-1">
            <label htmlFor="expertise" className="block text-sm font-medium text-gray-700 mb-1">
              Areas of Expertise
            </label>
            <div className="space-y-2">
              <div className="flex gap-2">
                <select
                  value={selectedExpertise}
                  onChange={(e) => setSelectedExpertise(e.target.value)}
                  className="flex-1 border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
                >
                  <option value="">Select a professional role</option>
                  {availableRoles.map((role) => (
                    <option key={role} value={role}>
                      {role}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={addExpertise}
                  disabled={!selectedExpertise}
                  className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Add
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {profile.expertise?.map((exp, index) => (
                  <span key={index} className="inline-flex items-center bg-indigo-100 text-indigo-800 text-sm font-medium px-3 py-1 rounded-full">
                    {exp}
                    <button
                      type="button"
                      onClick={() => removeExpertise(exp)}
                      className="ml-1.5 text-indigo-500 hover:text-indigo-700"
                      aria-label={`Remove ${exp}`}
                    >
                      <FaTimes size={12} />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-1 md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Availability
            </label>
            <div className="space-y-4">
              {Object.entries(profile.availability || {}).map(([day, data]) => (
                <div key={day} className="flex items-start">
                  <div className="flex items-center h-10 w-24">
                    <input
                      type="checkbox"
                      id={`${day}-available`}
                      checked={data.available}
                      onChange={() => toggleDayAvailability(day)}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <label htmlFor={`${day}-available`} className="ml-2 block text-sm font-medium text-gray-700 capitalize">
                      {day}
                    </label>
                  </div>
                  <div className="flex-1">
                    {data.available && (
                      <div className="space-y-2">
                        {data.hours?.map((time, idx) => (
                          <div key={idx} className="flex items-center space-x-2">
                            <input
                              type="time"
                              value={time.start}
                              onChange={(e) => updateAvailability(day, idx, 'start', e.target.value)}
                              className="border rounded-md px-2 py-1 text-sm w-24"
                            />
                            <span>to</span>
                            <input
                              type="time"
                              value={time.end}
                              onChange={(e) => updateAvailability(day, idx, 'end', e.target.value)}
                              className="border rounded-md px-2 py-1 text-sm w-24"
                            />
                            <button
                              type="button"
                              onClick={() => removeTimeSlot(day, idx)}
                              className="text-red-500 hover:text-red-700 ml-2"
                            >
                              <FaTimes size={14} />
                            </button>
                          </div>
                        ))}
                        <button
                          type="button"
                          onClick={() => addTimeSlot(day)}
                          className="text-sm text-indigo-600 hover:text-indigo-800 mt-1 flex items-center"
                        >
                          <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                          </svg>
                          Add time slot
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>


        </div>
      </div>

      {/* Services Section */}
      <div className="bg-white p-6 rounded-lg shadow mb-6">
        <h2 className="text-xl font-semibold mb-6 text-gray-800">Services Offered</h2>
        
        <div className="space-y-4">
          <div className="flex items-center space-x-2">
            <input
              type="text"
              value={newService}
              onChange={(e) => setNewService(e.target.value)}
              className="flex-1 border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="Add a service you offer"
              onKeyPress={(e) => e.key === 'Enter' && addService(e)}
            />
            <button
              type="button"
              onClick={addService}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            >
              Add
            </button>
          </div>
          
          <div className="flex flex-wrap gap-2 mb-4">
            {profile.services?.map((service, index) => (
              <div key={index} className="bg-blue-100 text-blue-800 text-sm font-medium px-3 py-1 rounded-full flex items-center">
                {service}
                <button
                  type="button"
                  onClick={() => removeService(index)}
                  className="ml-2 text-blue-500 hover:text-blue-700"
                >
                  <FaTimes size={12} />
                </button>
              </div>
            ))}
          </div>

          {/* Fee Input */}
          <div className="space-y-1 border-t pt-4">
            <label htmlFor="fee" className="block text-sm font-medium text-gray-700 mb-1">Fee (optional)</label>
            <div className="flex gap-2">
              <input 
                type="number" 
                id="fee"
                name="fee" 
                value={profile.fee || ''} 
                onChange={handleChange}
                className="w-32 border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="Enter amount"
                min="0"
                step="0.01"
              />
              <select 
                name="feeType" 
                value={profile.feeType || 'per session'} 
                onChange={handleChange}
                className="border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="per session">per session</option>
                <option value="per month">per month</option>
              </select>
            </div>
          </div>

          {/* Mode Selector */}
          <div className="space-y-1 border-t pt-4">
            <label htmlFor="mode" className="block text-sm font-medium text-gray-700 mb-1">Service Mode</label>
            <select
              id="mode"
              name="mode"
              value={profile.mode || 'Both'}
              onChange={handleModeChange}
              className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="Online">Online Only</option>
              <option value="Offline">In-Person Only</option>
              <option value="Both">Both Online & In-Person</option>
            </select>
          </div>
        </div>
      </div>

      {/* Experience Section */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h2 className="text-xl font-semibold mb-6 text-gray-800">Experience</h2>
        
        {profile.experience?.map((exp, index) => (
          <div key={index} className="mb-6 p-4 border rounded-lg bg-gray-50">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Job Title</label>
                <input
                  type="text"
                  value={exp.jobTitle || ''}
                  onChange={(e) => handleExperienceChange(index, 'jobTitle', e.target.value)}
                  className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="e.g., Senior Therapist"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
                <input
                  type="text"
                  value={exp.company || ''}
                  onChange={(e) => handleExperienceChange(index, 'company', e.target.value)}
                  className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Company name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">From</label>
                <input
                  type="text"
                  value={exp.from || ''}
                  onChange={(e) => handleExperienceChange(index, 'from', e.target.value)}
                  className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Year or Date"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">To</label>
                <input
                  type="text"
                  value={exp.to || ''}
                  onChange={(e) => handleExperienceChange(index, 'to', e.target.value)}
                  className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Year or 'Present'"
                />
              </div>
            </div>
            <div className="mb-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={exp.description || ''}
                onChange={(e) => handleExperienceChange(index, 'description', e.target.value)}
                rows="2"
                className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="Brief description of your role and achievements"
              />
            </div>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => removeExperience(index)}
                className="text-sm text-red-600 hover:text-red-800"
              >
                Remove Experience
              </button>
            </div>
          </div>
        ))}
        
        <button
          type="button"
          onClick={addNewExperience}
          className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm leading-4 font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          + Add Experience
        </button>
      </div>

      {/* Education Section */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h2 className="text-xl font-semibold mb-6 text-gray-800">Education</h2>
        
        {profile.education?.map((edu, index) => (
          <div key={index} className="mb-6 p-4 border rounded-lg bg-gray-50">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Degree</label>
                <input
                  type="text"
                  value={edu.degree || ''}
                  onChange={(e) => handleEducationChange(index, 'degree', e.target.value)}
                  className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="e.g., B.Ed Special Education"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Institution</label>
                <input
                  type="text"
                  value={edu.institution || ''}
                  onChange={(e) => handleEducationChange(index, 'institution', e.target.value)}
                  className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Institution name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
                <input
                  type="text"
                  value={edu.year || ''}
                  onChange={(e) => handleEducationChange(index, 'year', e.target.value)}
                  className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Year of completion"
                />
              </div>
            </div>
            <div className="mt-2 flex justify-end">
              <button
                type="button"
                onClick={() => removeEducation(index)}
                className="text-sm text-red-600 hover:text-red-800"
              >
                Remove Education
              </button>
            </div>
          </div>
        ))}
        
        <button
          type="button"
          onClick={addNewEducation}
          className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm leading-4 font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          + Add Education
        </button>
      </div>



      {/* Resume Section */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h2 className="text-xl font-semibold mb-6 text-gray-800">Resume</h2>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Upload Resume
            </label>
            <div className="mt-1 flex items-center">
              <input
                type="file"
                id="resume-upload"
                accept=".pdf,.doc,.docx"
                onChange={handleResumeUpload}
                className="hidden"
              />
              <label
                htmlFor="resume-upload"
                className="cursor-pointer bg-white py-2 px-3 border border-gray-300 rounded-md shadow-sm text-sm leading-4 font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Choose File
              </label>
              <span className="ml-4 text-sm text-gray-500">
                {profile.resumeFileName || 'No file chosen'}
              </span>
              {profile.resumeUrl && (
                <a
                  href={profile.resumeUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ml-2 text-sm text-indigo-600 hover:text-indigo-500"
                >
                  View Current
                </a>
              )}
            </div>
            <p className="mt-1 text-xs text-gray-500">
              PDF, DOC, or DOCX (Max 5MB)
            </p>
          </div>
        </div>
      </div>

      {/* Form Actions */}
      <div className="flex justify-end space-x-4 pt-4">
        <button
          type="button"
          onClick={toggleEditMode}
          className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </motion.form>
  );

  // Main component render
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <main className="py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Profile Header - LinkedIn Style */}
          <div className="bg-white rounded-lg shadow overflow-hidden mb-8">
            {/* Banner */}
            <div className="bg-gradient-to-r from-indigo-500 to-purple-600 h-40"></div>
            
            {/* Profile Content */}
            <div className="px-8 pb-6 -mt-16">
              {/* Profile Picture and Name Section */}
              <div className="relative">

                
                {/* Profile Picture */}
                <div className="relative mb-4">
                  <div 
                    className="h-32 w-32 rounded-full border-4 border-white bg-white shadow-lg overflow-hidden cursor-pointer group relative"
                    onClick={() => document.getElementById('profile-photo-upload').click()}
                  >
                    {currentUser?.photoURL ? (
                      <>
                        <img
                          src={currentUser.photoURL}
                          alt="Profile"
                          className="h-full w-full object-cover"
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 flex items-center justify-center transition-all duration-200 rounded-full">
                          <div className="bg-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                            </svg>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="h-full w-full bg-indigo-100 flex items-center justify-center">
                        <FaUserCircle className="h-20 w-20 text-indigo-400" />
                        <div className="absolute bottom-0 right-0 bg-indigo-600 text-white rounded-full p-2">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                        </div>
                      </div>
                    )}
                  </div>
                  <input
                    id="profile-photo-upload"
                    type="file"
                    accept="image/*"
                    onChange={handleProfilePhotoUpload}
                    className="hidden"
                  />
                </div>
                
                {/* Name and Title - Compact Layout */}
                <div className="mt-2 space-y-0.5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <h1 className="text-2xl font-bold text-gray-900">
                        {profile.fullName || 'Your Profile'}
                      </h1>
                      <button
                        onClick={editMode ? handleSave : toggleEditMode}
                        className="ml-2 text-gray-400 hover:text-indigo-600 transition-colors duration-200 -mt-1"
                        title={editMode ? 'Save changes' : 'Edit profile'}
                      >
                        {editMode ? (
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 13l4 4L19 7" />
                          </svg>
                        ) : (
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                          </svg>
                        )}
                      </button>
                    </div>
                    {!editMode && (
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => setShowShareModal(true)}
                          className="text-green-600 hover:text-green-800 text-sm font-medium flex items-center bg-green-50 hover:bg-green-100 px-3 py-1.5 rounded-full transition-colors duration-200"
                          title="Get Recommendations"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                          </svg>
                          Get Recommendations
                        </button>
                        <a
                          href={`/professional/${currentUser?.uid}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-indigo-600 hover:text-indigo-800 text-sm font-medium flex items-center bg-indigo-50 hover:bg-indigo-100 px-3 py-1.5 rounded-full transition-colors duration-200"
                          title="View Public Profile"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                          View Public Profile
                        </a>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center space-x-3 text-sm">
                    {profile.expertise && (
                      <span className="text-gray-600 font-medium">{profile.expertise}</span>
                    )}
                    {profile.city && (
                      <span className="text-gray-500">{profile.city}</span>
                    )}
                    {!isProfileComplete() && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        Profile Incomplete
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <AnimatePresence mode="wait">
            {editMode ? renderEditMode() : renderViewMode()}
          </AnimatePresence>
        </div>
      </main>

      <Footer />
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
      
      {/* Share Profile Modal */}
      <ShareProfileModal 
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        profile={{
          ...profile,
          id: currentUser?.uid,
          fullName: profile.fullName || 'Your Profile',
          photoURL: profile.photoURL || '',
          expertise: Array.isArray(profile.expertise) ? profile.expertise : [profile.expertise].filter(Boolean)
        }}
      />
    </div>
  );
};

export default ProfessionalProfile;
